import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { signIn, signOut } from '../services/AuthenticationService';

// Auth Context for managing user authentication state
interface User {
  sub: string;
  email: string;
  email_verified: boolean;
  'cognito:username': string;
  store_id?: string;
  'custom:store_id'?: string;
  [key: string]: any;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  storeId: string | null;
  staffId: string | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [storeId, setStoreId] = useState<string | null>(null);
  const [staffId, setStaffId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initializationRef = useRef(false);

  const clearAuthData = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('storeId');
    localStorage.removeItem('staffId');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setStoreId(null);
    setStaffId(null);
    setIsAuthenticated(false);
  };

  useEffect(() => {
    if (initializationRef.current) {
      return;
    }
    
    initializationRef.current = true;
    
    const initializeAuth = () => {
      try {
        const storedToken = localStorage.getItem('authToken');
        const storedStoreId = localStorage.getItem('storeId');
        const storedStaffId = localStorage.getItem('staffId');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
          try {
            const tokenParts = storedToken.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));

              if (payload.exp > Math.floor(Date.now() / 1000)) {
                const userData = JSON.parse(storedUser);
                setToken(storedToken);
                setUser(userData);
                setStoreId(storedStoreId);
                setStaffId(storedStaffId);
                setIsAuthenticated(true);
              } else {
                clearAuthData();
              }
            } else {
              clearAuthData();
            }
          } catch (decodeError) {
            clearAuthData();
          }
        }
      } catch (error) {
        clearAuthData();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
    
    return () => {
      // Cleanup if needed
    };
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const result = await signIn(email, password);

      if (result.newPasswordRequired) {
        setError('Password change required');
        return false;
      }

      if (result.user && result.token) {
        // Cast the user data to our User interface
        const userData = result.user as any; // The service returns a different User type
        
        // Store authentication data
        localStorage.setItem('authToken', result.token);
        if (result.refreshToken) {
          localStorage.setItem('refreshToken', result.refreshToken);
        }
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Extract and store storeId from user attributes
        const extractedStoreId = userData.store_id || userData['custom:store_id'] || null;
        if (extractedStoreId) {
          localStorage.setItem('storeId', extractedStoreId);
          setStoreId(extractedStoreId);
        }

        // Update state
        setToken(result.token);
        setUser(userData);
        setIsAuthenticated(true);
        
        return true;
      }

      setError('Invalid credentials');
      return false;
    } catch (err: any) {
      setError(err.message || 'Login failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await signOut();
    } catch (error) {
      // Handle signout error silently
    }
    clearAuthData();
    setError(null);
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    token,
    storeId,
    staffId,
    loading,
    error,
    login,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
